/**
 * Drag-to-pan and wheel-to-zoom, as arithmetic.
 *
 * The research tab is a map — thirty-four cards, five branches, fifteen lineages,
 * a measured 2,068 × 724 px of grid — and it used to be read through two
 * scrollbars. This is
 * the maths behind reading it the way Path of Exile's passive tree is read:
 * **drag anywhere to move around it, wheel to zoom toward whatever the pointer is
 * over.** None of it touches the DOM, so every rule below — the clamps, the
 * zoom-about-a-point, the pan bounds, the reveal — is testable without a browser,
 * and `pan-zoom.test.ts` is that test. `ResearchTree.vue` owns the listeners and
 * the one `transform` these numbers feed.
 *
 * ── One coordinate rule, and everything follows from it ──────────────────────
 *
 * The content is laid out once, untransformed, and a wrapper carries
 * `transform: translate(pan) scale(zoom)` with `transform-origin: 0 0`. So a
 * point in the grid's own layout coordinates lands on screen at
 *
 *     screen = pan + layout × zoom
 *
 * and `layout = (screen − pan) / zoom` is the inverse. Both are stated once here
 * (`toScreen` / `toContent`) rather than open-coded at four call sites, because
 * every function below is one of the two composed with a constraint.
 *
 * **Zoom is a transform and must stay one.** Re-laying the grid out per zoom step
 * — larger fonts, wider tracks — would reflow thirty-four content-sized cards on
 * every wheel notch *and* move the cards, and the connectors are brackets
 * measured from where the cards ended up. A transform leaves the layout, and
 * therefore those measurements, untouched: the tree is measured once and is
 * correct at every zoom. That is the whole reason this file is arithmetic over a
 * fixed layout rather than a set of CSS variables.
 */

export interface Vec {
  x: number;
  y: number;
}

export interface Size {
  w: number;
  h: number;
}

/** A box in the content's own layout coordinates. */
export interface Rect extends Vec, Size {}

/** Where the content is and how big it is drawn. */
export interface View {
  pan: Vec;
  zoom: number;
}

/**
 * How far out the tree may be pushed, and where the number comes from.
 *
 * The 34-node grid measured 2,068 × 724 px and its floor was 0.35; the 102-node
 * tripling made it 5,374 px of four-row wall and dropped the floor to 0.10; the
 * 105-node deepening re-tiered it to **3,084 × 1,328 px** (seven rows,
 * 23 lineages), measured in the live tab. The research tab's smallest port is
 * **627 × 281 px on a 667 × 375 landscape phone**, and fitting the deepened
 * grid there needs **~0.19** — the binding axis is the *height* now, which it
 * never was while the tree was a wall — against ~0.39 on a 1,280 × 720 laptop.
 * The floor stays at 0.10: comfortably below every fit, so it is a floor rather
 * than a constraint, with a little further out available for anyone who wants
 * the shape and nothing else. (`fitView` clamps to this floor, so a floor above
 * the fit ratio would silently cut the far rows off every Fit — which is
 * exactly what a 0.12 floor did to the phone port once, 18 px of tree standing
 * outside every Fit.)
 */
export const MIN_ZOOM = 0.1;

/**
 * How far in, and where *that* number comes from.
 *
 * A node icon is a 256 px WebP drawn at 5 rem — 80 px — so there are 3.2× of
 * real pixels behind a card before the browser starts inventing any. 2.5× stays
 * inside that with margin, and it is already generous: a 118 px card is drawn
 * 295 px wide with its name at ~31 px, which is larger than anything else in the
 * garage. Past the icon's own resolution the tab would be selling a blurry
 * picture as detail.
 */
export const MAX_ZOOM = 2.5;

/**
 * Fitting never magnifies past 1:1.
 *
 * A tree that already fits is shown at the size the cards were designed at; the
 * alternative is a Fit button that blows four cards up to fill a wide monitor.
 */
export const FIT_MAX_ZOOM = 1;

/**
 * How far the pointer must travel before a press becomes a drag.
 *
 * **A threshold rather than a second button, and the arena's argument for the
 * opposite does not transfer.** *One button each* (`CLAUDE.md`) split firing from
 * looking because the press committed a *shell* before anyone could know which
 * gesture it was — the cost was paid at `pointerdown`, so no amount of waiting
 * could recover it. Nothing here is spent at `pointerdown`: a click on a card
 * opens the panel that hovering already opened, and the only spend on the tab is
 * behind a button inside that panel *and* behind the confirmation dialog. So the
 * gesture can be classified at the first movement instead, and a drag can start
 * anywhere — which is the actual request, and what a map should do. Four pixels
 * is under the slop of a deliberate click and well inside a deliberate drag.
 *
 * The press that turns into a drag still has to have its `click` swallowed, or a
 * pan that happens to end on the Research button would open a purchase dialog.
 * That is the component's job, not this file's.
 */
export const DRAG_THRESHOLD_PX = 4;

/** Air left around the tree when it is fitted, so no card is flush to an edge. */
export const FIT_MARGIN_PX = 14;

/** Air left around a card that had to be brought into view. */
export const REVEAL_MARGIN_PX = 20;

/**
 * Wheel sensitivity, as an exponent so a notch is a *ratio*.
 *
 * Zoom has to be geometric: at a fixed step per notch, one notch out of 2.5×
 * would be imperceptible and one notch out of 0.35× would cross half the range.
 * Chrome's ordinary notch is 100 px, which lands at ×1.20.
 */
const ZOOM_PER_WHEEL_PX = 0.0018;

/** A wheel notch reported in lines rather than pixels. */
const LINE_PX = 16;
/** A wheel notch reported in pages. Deliberately not a real page height: this is
 *  only here so a page-mode device zooms *a lot* rather than infinitely. */
const PAGE_PX = 400;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** A layout point, on screen. Screen coordinates are relative to the viewport. */
export function toScreen(view: View, layout: Vec): Vec {
  return { x: view.pan.x + layout.x * view.zoom, y: view.pan.y + layout.y * view.zoom };
}

/** The layout point under a screen point — the inverse, and the one the wheel needs. */
export function toContent(view: View, screen: Vec): Vec {
  return { x: (screen.x - view.pan.x) / view.zoom, y: (screen.y - view.pan.y) / view.zoom };
}

export function clampZoom(zoom: number): number {
  // A non-finite zoom would put `NaN` in the transform and lose the whole tree,
  // and every caller's input arrives from a device event or a division.
  return Number.isFinite(zoom) ? clamp(zoom, MIN_ZOOM, MAX_ZOOM) : 1;
}

/**
 * The zoom a wheel event asks for, clamped.
 *
 * `deltaMode` is honoured because a trackpad reports pixels, a mouse wheel on
 * some platforms reports lines, and a page-mode device would otherwise move by
 * three thousandths of nothing.
 */
export function wheelZoom(zoom: number, deltaY: number, deltaMode = 0): number {
  const px = deltaY * (deltaMode === 1 ? LINE_PX : deltaMode === 2 ? PAGE_PX : 1);
  // Away from the user is a negative delta and means *in*, which is what every
  // map in every browser does.
  return clampZoom(zoom * Math.exp(-px * ZOOM_PER_WHEEL_PX));
}

/**
 * Keeps the tree on screen.
 *
 * Two cases, one expression. When the drawn content is **smaller** than the
 * viewport the pan may run from 0 to the slack, so it is always wholly visible.
 * When it is **larger** the pan runs from the (negative) slack to 0, so an edge
 * of the content can never come inside the viewport — every pixel of the
 * viewport is content, and the tree can therefore never be dragged away and
 * lost. `[min(0, slack), max(0, slack)]` is both.
 *
 * A zero-sized viewport (a tab that has not been laid out yet) collapses the
 * range to a point rather than producing `NaN`.
 */
export function clampPan(pan: Vec, content: Size, viewport: Size, zoom: number): Vec {
  const slackX = viewport.w - content.w * zoom;
  const slackY = viewport.h - content.h * zoom;
  return {
    x: clamp(pan.x, Math.min(0, slackX), Math.max(0, slackX)),
    y: clamp(pan.y, Math.min(0, slackY), Math.max(0, slackY)),
  };
}

/**
 * Zooms about a point, which is the difference between a map and a viewer.
 *
 * The layout point under the pointer is held still: read it before, put it back
 * after. Zooming about the *centre* instead walks whatever you are inspecting out
 * of frame exactly when you lean in on it, which is the thing that makes a viewer
 * feel broken — and it is the reason this takes a pointer at all rather than
 * being a function of one number.
 *
 * The pan is clamped afterwards, so zooming out at the edge of the tree pulls it
 * back into the viewport rather than leaving a band of nothing.
 */
export function zoomAbout(
  view: View,
  pointer: Vec,
  nextZoom: number,
  content: Size,
  viewport: Size,
): View {
  const zoom = clampZoom(nextZoom);
  const anchor = toContent(view, pointer);
  const pan = { x: pointer.x - anchor.x * zoom, y: pointer.y - anchor.y * zoom };
  return { zoom, pan: clampPan(pan, content, viewport, zoom) };
}

/**
 * A two-finger pinch, as one pure step.
 *
 * Always computed **from the gesture's own start** — the view and the two touch
 * points captured when the second finger landed — never incrementally from the
 * previous frame. An incremental pinch multiplies a ratio per event and drifts
 * by its own rounding; anchored to the start, letting go where you began puts
 * the tree back exactly where it was.
 *
 * The zoom is the ratio of the finger separations, applied to the *starting*
 * zoom. The pan holds the layout point that was under the starting midpoint
 * under the **current** midpoint — so a pinch is also a drag, which is what two
 * fingers on a map mean everywhere: spread to lean in, and carry the tree with
 * the gesture while doing it. Both halves land in the same clamps as the wheel
 * and the one-finger drag, so a pinch can no more lose the tree than they can.
 *
 * Degenerate input — the two starting points identical, a non-finite ratio —
 * resolves to the starting view rather than to `NaN` in a transform.
 */
export function pinchView(
  start: { view: View; a: Vec; b: Vec },
  a: Vec,
  b: Vec,
  content: Size,
  viewport: Size,
): View {
  const fromDist = Math.hypot(start.b.x - start.a.x, start.b.y - start.a.y);
  const toDist = Math.hypot(b.x - a.x, b.y - a.y);
  if (fromDist <= 0) return start.view;
  const ratio = toDist / fromDist;
  if (!Number.isFinite(ratio) || ratio <= 0) return start.view;

  const zoom = clampZoom(start.view.zoom * ratio);
  const midFrom = { x: (start.a.x + start.b.x) / 2, y: (start.a.y + start.b.y) / 2 };
  const midTo = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const anchor = toContent(start.view, midFrom);
  const pan = { x: midTo.x - anchor.x * zoom, y: midTo.y - anchor.y * zoom };
  return { zoom, pan: clampPan(pan, content, viewport, zoom) };
}

/**
 * The whole tree, centred, at the largest zoom that shows all of it.
 *
 * This is what the Fit control does and what the tab opens on. `maxZoom` is
 * `FIT_MAX_ZOOM` so a tree that already fits is not blown up to fill the window;
 * the floor is `MIN_ZOOM`, so on a viewport too small even for that the result is
 * the closest honest thing — the middle of the tree, at the smallest readable
 * scale, with the pan free to reach the rest.
 */
export function fitView(content: Size, viewport: Size, maxZoom = FIT_MAX_ZOOM): View {
  const usableW = Math.max(1, viewport.w - FIT_MARGIN_PX * 2);
  const usableH = Math.max(1, viewport.h - FIT_MARGIN_PX * 2);
  const byWidth = content.w > 0 ? usableW / content.w : maxZoom;
  const byHeight = content.h > 0 ? usableH / content.h : maxZoom;
  const zoom = clampZoom(Math.min(maxZoom, byWidth, byHeight));
  const pan = {
    x: (viewport.w - content.w * zoom) / 2,
    y: (viewport.h - content.h * zoom) / 2,
  };
  return { zoom, pan: clampPan(pan, content, viewport, zoom) };
}

/**
 * Brings a box into view, moving as little as possible — or centring it.
 *
 * Two callers, and they want different things. **Focus wants `nearest`**: Tab
 * moving from one card to the next must not swing the whole tree, and a card
 * already on screen must not move at all — a keyboard user reading down a branch
 * would otherwise get a lurch per keystroke. **The shop's pointer wants
 * `centre`**: a player sent here from a locked hull card is being *shown* a node,
 * and the middle of the viewport is where you show somebody something.
 *
 * This is what replaced `scrollIntoView`, which stopped being available the
 * moment the tab stopped scrolling. Keeping the keyboard able to reach every one
 * of the thirty-four cards is not optional, so the reveal is explicit rather than
 * inherited from the browser.
 */
export function panToReveal(
  view: View,
  rect: Rect,
  content: Size,
  viewport: Size,
  mode: 'nearest' | 'centre' = 'nearest',
): Vec {
  if (mode === 'centre') {
    return clampPan(
      {
        x: viewport.w / 2 - (rect.x + rect.w / 2) * view.zoom,
        y: viewport.h / 2 - (rect.y + rect.h / 2) * view.zoom,
      },
      content,
      viewport,
      view.zoom,
    );
  }

  const near = toScreen(view, rect);
  const far = { x: near.x + rect.w * view.zoom, y: near.y + rect.h * view.zoom };
  const nudge = (lo: number, hi: number, limit: number): number => {
    // Too big to fit at this zoom: line the leading edge up rather than
    // oscillating between two impossible constraints.
    if (hi - lo > limit - REVEAL_MARGIN_PX * 2) return REVEAL_MARGIN_PX - lo;
    if (lo < REVEAL_MARGIN_PX) return REVEAL_MARGIN_PX - lo;
    if (hi > limit - REVEAL_MARGIN_PX) return limit - REVEAL_MARGIN_PX - hi;
    return 0;
  };

  return clampPan(
    {
      x: view.pan.x + nudge(near.x, far.x, viewport.w),
      y: view.pan.y + nudge(near.y, far.y, viewport.h),
    },
    content,
    viewport,
    view.zoom,
  );
}
